import { useEffect } from "react";
import { htmlLang, isValidUsername } from "@lorevok/domain";
import type { Page } from "../types.js";
import { getLanguage, useLanguage, type TranslateFn } from "./i18n/store.js";
import { novelHref } from "./links.js";
import { buildPath, parseRoute } from "./route.js";
import { siteInfo } from "./siteInfo.js";
import { isStagingDeploy } from "./stagingBuild.js";

// What search engines and the browser tab read about the page on screen:
// <title>, the meta description, the canonical link, og:url / og:title /
// og:description, <html lang> and — on private and missing pages only —
// <meta name="robots" content="noindex">.
//
// The app is one index.html for every route, so the head is rewritten on every
// navigation. Two layers write it: App registers a ROUTE layer for whatever page
// is on screen (routePageMeta), and the novel and author pages register a PAGE
// layer once their data arrives (novelPageMeta, authorPageMeta), which wins
// while they are mounted. Link previews come from the API's crawler pages
// (apps/api/src/seoPages.ts), whose canonical addresses these match.

export interface PageMeta {
  /** The whole document title. */
  title: string;
  description: string;
  /** The page's canonical path ("/catalog"); null = no canonical (a private or missing page). */
  path: string | null;
  /** Keep the page out of search results. */
  noindex?: boolean;
  /** `<html lang>`: the page's content language; the UI language when absent. */
  lang?: string;
}

export const SITE_NAME = "Lorvok";

/** "<name> — Lorvok". */
export function pageTitle(name: string): string {
  return `${name} — ${SITE_NAME}`;
}

function clip(text: string, max: number): string {
  const clean = text.replace(/\s+/g, " ").trim();
  return clean.length <= max ? clean : `${clean.slice(0, max - 1).trimEnd()}…`;
}

/** Private and utility routes: never in search results. */
export const NOINDEX_PAGES: ReadonlySet<Page> = new Set<Page>([
  "editor",
  "create",
  "status",
  "profile",
  "admin",
  "auth",
  "showcase",
  "genloop"
]);

/** Whether the router knows an address; anything else falls back to the landing page (a soft 404). */
export function isKnownPath(pathname: string): boolean {
  return pathname.split("/").every((segment) => !segment) || parseRoute(pathname).page !== "landing";
}

/** The canonical absolute URL of a site path. */
export function canonicalUrl(path: string, origin: string = siteInfo.siteUrl): string {
  return `${origin.replace(/\/+$/, "")}${path}`;
}

export interface RouteMetaContext {
  /** The URL segment of the novel a detail/player route shows (its link key or id). */
  novelSegment?: string | null;
  /** The /u/ key of the author page on screen. */
  authorKey?: string | null;
  /** The address was one the router does not know (it shows the landing page). */
  unknownPath?: boolean;
}

/**
 * The route layer: every page's title, description and canonical, before any
 * page data. The novel and author pages refine theirs once loaded.
 */
export function routePageMeta(page: Page, t: TranslateFn, context: RouteMetaContext = {}): PageMeta {
  const fallbackDescription = t("meta.landingDescription");
  if (NOINDEX_PAGES.has(page)) {
    const names: Partial<Record<Page, string>> = {
      editor: t("meta.editorTitle"),
      create: t("meta.createTitle"),
      status: t("status.title"),
      profile: t("nav.myNovels"),
      admin: t("nav.admin"),
      auth: t("nav.signIn")
    };
    const name = names[page];
    return { title: name ? pageTitle(name) : SITE_NAME, description: fallbackDescription, path: null, noindex: true };
  }
  switch (page) {
    case "catalog":
      return { title: pageTitle(t("meta.catalogTitle")), description: t("meta.catalogDescription"), path: "/catalog" };
    case "pricing":
      return { title: pageTitle(t("footer.pricing")), description: t("meta.pricingDescription"), path: "/pricing" };
    case "terms":
      return { title: pageTitle(t("footer.terms")), description: t("meta.termsDescription"), path: "/terms" };
    case "privacy":
      return { title: pageTitle(t("footer.privacy")), description: t("meta.privacyDescription"), path: "/privacy" };
    case "refund":
      return { title: pageTitle(t("footer.refund")), description: t("meta.refundDescription"), path: "/refund" };
    case "detail":
    case "player":
      // A novel's canonical is always its detail page, the player's included.
      return {
        title: SITE_NAME,
        description: t("meta.novelDescription"),
        path: context.novelSegment ? buildPath("detail", context.novelSegment) : null
      };
    case "author":
      return {
        title: pageTitle(t("author.eyebrow")),
        description: fallbackDescription,
        path: context.authorKey ? buildPath("author", context.authorKey) : null
      };
    default:
      // The landing page — also what an unknown address shows, which is no page of its own.
      return context.unknownPath
        ? { title: t("meta.landingTitle"), description: fallbackDescription, path: null, noindex: true }
        : { title: t("meta.landingTitle"), description: fallbackDescription, path: "/" };
  }
}

/**
 * A loaded novel's page (detail or player): its title and description, its
 * canonical `/novel/<slug>-<code>` (the API's crawler page uses the same one),
 * and its own language. Anything but a public novel stays out of the index.
 */
export function novelPageMeta(
  novel: { id: string; title: string; description?: string; visibility: string; language?: string; short_code?: string },
  t: TranslateFn,
  lang?: string
): PageMeta {
  const indexable = novel.visibility === "public";
  const contentLang = htmlLang(lang ?? novel.language);
  return {
    title: pageTitle(clip(novel.title, 90)),
    description: clip(novel.description || t("meta.novelDescription"), 200),
    path: indexable ? novelHref("detail", novel) : null,
    noindex: !indexable,
    ...(contentLang ? { lang: contentLang } : {})
  };
}

/** An author's page: canonical `/u/<username>`; out of the index while they have no public novel. */
export function authorPageMeta(
  author: { id: string; username: string; displayName: string; bio: string; publicNovels: number },
  t: TranslateFn
): PageMeta {
  const name = clip(author.displayName, 60);
  return {
    title: pageTitle(t("meta.authorTitle", { name })),
    description: clip(author.bio || t("meta.authorDescription", { name }), 200),
    path: buildPath("author", isValidUsername(author.username) ? author.username : author.id),
    noindex: author.publicNovels === 0
  };
}

/** A page that could not be found (a missing novel or author, an unreadable link): no canonical, noindex. */
export function missingPageMeta(title: string, t: TranslateFn): PageMeta {
  return { title: pageTitle(title), description: t("meta.landingDescription"), path: null, noindex: true };
}

// ── Writing the head ────────────────────────────────────────────────────────

/** A staging build ships its own "noindex, nofollow" (stagingBuild.ts), which must stay. */
const MANAGE_ROBOTS = !isStagingDeploy(import.meta.env.VITE_DEPLOY_ENV as string | undefined);

function setMetaTag(doc: Document, attr: "name" | "property", key: string, value: string | null): void {
  const tags = [...doc.head.querySelectorAll<HTMLMetaElement>(`meta[${attr}="${key}"]`)];
  if (value === null) {
    for (const tag of tags) tag.remove();
    return;
  }
  const tag = tags[0] ?? doc.head.appendChild(doc.createElement("meta"));
  tag.setAttribute(attr, key);
  tag.setAttribute("content", value);
}

function setCanonical(doc: Document, href: string | null): void {
  const links = [...doc.head.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]')];
  if (href === null) {
    for (const link of links) link.remove();
    return;
  }
  const link = links[0] ?? doc.head.appendChild(doc.createElement("link"));
  link.setAttribute("rel", "canonical");
  link.setAttribute("href", href);
  for (const extra of links.slice(1)) extra.remove();
}

export interface ApplyOptions {
  doc?: Document;
  origin?: string;
  /** Whether to write the robots tag (false on a staging build). */
  manageRobots?: boolean;
}

/** Writes one page's meta into the document head. */
export function applyPageMeta(meta: PageMeta, options: ApplyOptions = {}): void {
  const doc = options.doc ?? (typeof document !== "undefined" ? document : null);
  if (!doc) return;
  doc.title = meta.title;
  setMetaTag(doc, "name", "description", meta.description);
  setMetaTag(doc, "property", "og:title", meta.title);
  setMetaTag(doc, "property", "og:description", meta.description);
  setMetaTag(doc, "name", "twitter:title", meta.title);
  setMetaTag(doc, "name", "twitter:description", meta.description);
  // A noindexed page names no canonical: the two would contradict each other.
  const canonical = meta.path && !meta.noindex ? canonicalUrl(meta.path, options.origin) : null;
  setCanonical(doc, canonical);
  setMetaTag(doc, "property", "og:url", canonical);
  if (options.manageRobots ?? MANAGE_ROBOTS) {
    // One robots tag while the page is private or missing, none otherwise —
    // including the one the API's page for an unlisted novel came with.
    setMetaTag(doc, "name", "robots", meta.noindex ? "noindex" : null);
  }
  doc.documentElement.lang = meta.lang ?? getLanguage();
}

// ── The layers ──────────────────────────────────────────────────────────────

interface Registration {
  layer: number;
  order: number;
  meta: PageMeta;
}

const registrations = new Set<Registration>();
let registrationOrder = 0;

/** Applies the winning registration: the page layer over the route layer, then the latest. */
function applyWinner(): void {
  let winner: Registration | null = null;
  for (const entry of registrations) {
    if (!winner || entry.layer > winner.layer || (entry.layer === winner.layer && entry.order > winner.order)) {
      winner = entry;
    }
  }
  if (winner) applyPageMeta(winner.meta);
}

/**
 * Registers this component's meta while it is mounted (null registers
 * nothing: the layer below shows). `route` is App's layer; `page` is a page's
 * own, which wins. Re-applied when the UI language changes, since the i18n
 * store resets `<html lang>` to it.
 */
export function usePageMeta(meta: PageMeta | null, layer: "route" | "page" = "page"): void {
  const uiLanguage = useLanguage();
  const key = meta ? JSON.stringify(meta) : null;
  useEffect(() => {
    if (key === null) return;
    const entry: Registration = {
      layer: layer === "page" ? 1 : 0,
      order: (registrationOrder += 1),
      meta: JSON.parse(key) as PageMeta
    };
    registrations.add(entry);
    applyWinner();
    return () => {
      registrations.delete(entry);
      applyWinner();
    };
  }, [key, layer, uiLanguage]);
}
